import { useState } from "react";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import type { SpreadsheetColumnFilter } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-region-view";
import { ConditionFilterEditor } from "./condition-filter-editor";
import { ValueFilterEditor } from "./value-filter-editor";

interface SpreadsheetColumnFilterEditorProps {
  values: string[];
  filter: SpreadsheetColumnFilter;
  onChange: (filter: SpreadsheetColumnFilter) => void;
}

type FilterMode = "values" | "condition";

export const SpreadsheetColumnFilterEditor = ({
  values,
  filter,
  onChange,
}: SpreadsheetColumnFilterEditorProps) => {
  const [mode, setMode] = useState<FilterMode>(
    filter.condition === null ? "values" : "condition",
  );
  const hasValueSelection =
    filter.selectedValues !== null &&
    filter.selectedValues.length !== values.length;
  const hasCondition =
    filter.condition !== null && filter.conditionValue !== "";

  return (
    <Stack
      gap="sm"
      fullWidth
      className="min-h-0 flex-1 overflow-hidden border-t border-stroke-tertiary px-4 py-3"
    >
      <div className="grid w-full grid-cols-2 gap-1 rounded-lg bg-surface-secondary p-1">
        {(
          [
            ["values", "Values", hasValueSelection],
            ["condition", "Condition", hasCondition],
          ] as const
        ).map(([tab, label, isSet]) => (
          <button
            key={tab}
            type="button"
            aria-pressed={mode === tab}
            onClick={() => setMode(tab)}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors",
              {
                "bg-card font-medium text-primary-text shadow-sm": mode === tab,
                "text-secondary-text hover:text-primary-text": mode !== tab,
              },
            )}
          >
            {label}
            {/* Same story as the summary line: `bg-accent` is shadcn's
                unwired near-white, which put this "something is set on this
                tab" dot at 1.03:1 against the tab it sits on. */}
            {isSet && (
              <span className="size-1.5 rounded-full bg-interactive-text" />
            )}
          </button>
        ))}
      </div>

      {mode === "values" ? (
        <ValueFilterEditor
          values={values}
          filter={filter}
          onChange={onChange}
        />
      ) : (
        <ConditionFilterEditor filter={filter} onChange={onChange} />
      )}
    </Stack>
  );
};
